import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { get, post, refreshSession, setSession, getRefreshToken, onSessionLost, type Me } from '../lib/api.ts';
import { startNativePush, stopNativePush } from '../lib/nativePush.ts';
import { setBioPref } from '../lib/appLock.ts';
import { dropPushSubscription } from '../notifications/systemNotify.ts';

interface SessionValue {
  me: Me | null; loading: boolean;
  /** انتهت الجلسة من تلقاء نفسها (رمز التجديد رُفض) — لا خروجًا إراديًا. تُعرض على شاشة الدخول ثم تُمسح. */
  expired: boolean;
  /**
   * أُغلقت لعدم الاستعمال (قرار المالك 22/09/2026) — لا خروجًا إراديًا ولا انتهاءَ
   * رمز. وتُقال لصاحبها على شاشة الدخول كي يعرف لماذا خرج، فلا يظنّ عطلًا.
   */
  idle: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** إنهاءُ الجلسة لهجر الشاشة — خروجٌ كامل، ويُطلب بعده كلمة المرور من جديد. */
  signOutIdle: () => Promise<void>;
  /** يعيد قراءة /me — بعد تغيير كلمة المرور الإلزامي يسقط must_change_password فتُفتح الشاشات */
  reload: () => Promise<void>;
}
const Ctx = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [expired, setExpired] = useState(false);
  const [idle, setIdle] = useState(false);

  // ضياع الجلسة أثناء العمل (401 على التجديد): تُسقَط الجلسة فورًا وتعود شاشة الدخول بسببها
  useEffect(() => onSessionLost(() => { setMe(null); setExpired(true); }), []);

  // استعادة الجلسة بعد تحديث الصفحة عبر توكن التجديد
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!getRefreshToken()) { setLoading(false); return; }
      /**
       * **الشبكةُ المقطوعة ليست جلسةً منتهية** (مراجعة 23/09/2026): التطبيقُ المثبَّت يُفتح بلا
       * إشارة، أو يُفتح والخادمُ يُعاد تشغيله — فكان يمسح الجلسة ويطلب الدخول من جديد. فيُعاد
       * المحاولةُ بمهلٍ متزايدة ما دام الرمز موجودًا، ولا يُمسح إلا إن رفضه الخادم نفسه (401).
       */
      for (let attempt = 0; alive; attempt++) {
        try {
          if (await refreshSession()) {
            const u = await get<Me>('/me');
            if (alive) setMe(u);
            break;
          }
          if (!getRefreshToken()) { if (alive) setExpired(true); break; }     // رفضه الخادم (401) فمُسح
        } catch { /* الشبكة أو الخادم — يُعاد */ }
        if (attempt >= 6) break;                                             // دقيقتان تقريبًا ثم شاشة الدخول (والرمز باقٍ)
        await new Promise(r => setTimeout(r, Math.min(20_000, 2_000 * 2 ** attempt)));
      }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const r = await post<{ accessToken: string; refreshToken: string; user: Me }>('/auth/login', { email, password });
    setSession(r.accessToken, r.refreshToken);
    setMe(await get<Me>('/me'));
    setExpired(false);
    setIdle(false);
  }, []);

  // إشعارات الموبايل (0173): كلّما صار في البرنامج مستخدمٌ مسجَّل — دخولًا أو استعادة جلسة — يُسجَّل موبايله
  useEffect(() => { if (me) void startNativePush().catch(() => undefined); }, [me?.id]);   // eslint-disable-line react-hooks/exhaustive-deps

  const signOut = useCallback(async () => {
    // والجلسة ما زالت صالحة: لا تصل إشعاراته لموبايلٍ ولا لمتصفّحٍ خرج منه (0174 يغلق الباقي من الخادم)
    await Promise.all([stopNativePush(), dropPushSubscription(endpoint => post('/me/push/unsubscribe', { endpoint }))]);
    setBioPref(null);                       // ومن يدخل بعده على نفس الموبايل يُسأل عن بصمته هو
    try { await post('/auth/logout', { refreshToken: getRefreshToken() }); } catch { /* الجلسة منتهية أصلًا */ }
    setSession(null, null);
    setMe(null);
    setExpired(false);          // خروج إرادي — لا يُعرض كانتهاء جلسة
    setIdle(false);
  }, []);

  const signOutIdle = useCallback(async () => { await signOut(); setIdle(true); }, [signOut]);

  const reload = useCallback(async () => { setMe(await get<Me>('/me')); }, []);

  const value = useMemo(
    () => ({ me, loading, expired, idle, signIn, signOut, signOutIdle, reload }),
    [me, loading, expired, idle, signIn, signOut, signOutIdle, reload],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useSession must be used inside SessionProvider');
  return v;
}

/**
 * الجلسة **إن وُجدت**، وإلا `null` بلا رمي.
 *
 * لقسمٍ يعرض شيئًا إضافيًّا لصاحب الجلسة ويعمل تمامًا بدونه: من لا يعرف من أنت لا يعرض
 * بطاقتك، ولا يُسقط الشاشة كلها. و`useSession` تبقى هي الصارمة لمن لا معنى له بلا جلسة.
 */
export function useOptionalSession(): SessionValue | null {
  return useContext(Ctx) ?? null;
}
export const isAdmin = (me: Me | null): boolean => me?.role === 'ADMIN';
